import { ReactNode } from "react";
import {
  Info,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  LucideIcon,
} from "lucide-react";
import styles from "./Alert.module.css";

type Variant = "info" | "success" | "warn" | "danger";

interface Props {
  variant: Variant;
  /** The banner's heading. A node rather than a string because a title often
   *  names a config key or a request type, and those are written as `<code>`
   *  everywhere else on the page they appear. */
  title?: ReactNode;
  /**
   * Replace the variant's default glyph. Use it only when a specific icon
   * carries meaning the variant cannot — the transfer export card's warn
   * alert is about secrets, so it draws a padlock
   * (.codelane/artifacts/t-27/transfer-export.html L361). The tint still
   * comes from the variant.
   */
  icon?: ReactNode;
  /**
   * One control at the alert's right edge, on the banner's own row rather than
   * under its text — the way the wipe banner offers "View progress"
   * (docs/mockups/project-wipe.html).
   *
   * A slot rather than the caller's own layout because the alignment is the
   * alert's business: put through `children` the button lands inside the text
   * column, under the body, and every page that wanted it beside the title
   * would restate the same three flex rules to pull it back out.
   */
  action?: ReactNode;
  /**
   * Override the live-region setting `role="alert"` implies — which is
   * `assertive` and, because the role also implies `aria-atomic`, a re-reading
   * of the whole banner on every change inside it.
   *
   * `"off"` is for the alert whose text keeps moving: an assertive region that
   * changes once a second interrupts the screen-reader user hundreds of times
   * and they never finish a sentence. It silences the announcement only — the
   * banner stays in the accessibility tree, word for word, for anyone who
   * navigates to it.
   */
  "aria-live"?: "off" | "polite" | "assertive";
  children?: ReactNode;
}

const ICONS: Record<Variant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  danger: AlertCircle,
};

export function Alert({
  variant,
  title,
  icon,
  action,
  "aria-live": live,
  children,
}: Props) {
  const Icon = ICONS[variant];
  return (
    <div
      className={`${styles.alert} ${styles[variant]}`}
      role="alert"
      // Undefined leaves the attribute off entirely, so every other alert on
      // the site keeps exactly the behaviour the role gives it.
      aria-live={live}
    >
      <span className={styles.icon} aria-hidden="true">
        {icon ?? <Icon size={16} />}
      </span>
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        {children && <div className={styles.body}>{children}</div>}
      </div>
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}
